import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import replayerTvIcon from '../../images/replayer_tv_icon.svg';

const PublicPartnerSkyworkVideoCard = ({card}) => {
  const preferredThumbnail = card.thumbnailUrl || card.thumbnailFallbackUrl || null;
  const [resolvedThumbnail, setResolvedThumbnail] = useState(preferredThumbnail);

  useEffect(() => {
    setResolvedThumbnail(card.thumbnailUrl || card.thumbnailFallbackUrl || null);
  }, [card.thumbnailUrl, card.thumbnailFallbackUrl]);

  const handleThumbnailError = () => {
    if (
      card.thumbnailFallbackUrl
      && resolvedThumbnail !== card.thumbnailFallbackUrl
    ) {
      setResolvedThumbnail(card.thumbnailFallbackUrl);
      return;
    }
    setResolvedThumbnail(null);
  };

  return (
    <article
      className={`replayer-club-page__video-card replayer-club-page__video-card--${card.tone || 'default'}`}
      data-field-sport={card.fieldSport}
      data-pbvision-eligible={card.pbvisionEligible}
    >
      <div className={`replayer-club-page__video-media ${resolvedThumbnail ? '' : 'replayer-club-page__video-media--fallback'}`}>
        {resolvedThumbnail ? (
          <img
            src={resolvedThumbnail}
            alt={card.thumbnailAlt}
            loading="lazy"
            decoding="async"
            onError={handleThumbnailError}
          />
        ) : (
          <div className="replayer-club-page__video-fallback" aria-hidden="true">
            <img src={replayerTvIcon} alt=""/>
            <span>REPLAYERTV</span>
          </div>
        )}
        <div className="replayer-club-page__video-media-shade"/>
        <div className="replayer-club-page__video-badges">
          {card.badges.map(badge => (
            <span
              className={`replayer-club-page__video-badge replayer-club-page__video-badge--${badge.tone || 'default'}`}
              key={badge.id}
            >
              {badge.label}
            </span>
          ))}
        </div>
        {card.showPlayIcon && <span className="replayer-club-page__video-play" aria-hidden="true">▶</span>}
        {card.duration && <span className="replayer-club-page__video-duration">{card.duration}</span>}
      </div>

      <div className="replayer-club-page__video-body">
        <div className="replayer-club-page__video-source">
          {card.logoUrl && <img src={card.logoUrl} alt={card.logoAlt} loading="lazy" decoding="async"/>}
          <span>{card.sourceLabel}</span>
        </div>
        <h3>{card.title}</h3>
        {card.description && <p className="replayer-club-page__video-description">{card.description}</p>}
        {card.metadata.length > 0 && (
          <ul className="replayer-club-page__video-metadata" aria-label={card.metadataAriaLabel}>
            {card.metadata.map(entry => <li key={entry.id}>{entry.value}</li>)}
          </ul>
        )}
        {card.accessSummary && <p className="replayer-club-page__video-access">{card.accessSummary}</p>}

        <div className="replayer-club-page__video-actions">
          <button
            className={`replayer-club-page__video-primary replayer-club-page__video-primary--${card.primaryAction.tone || 'default'}`}
            disabled={card.primaryAction.disabled}
            onClick={card.primaryAction.onClick}
            type="button"
          >
            {card.primaryAction.label}
          </button>
          {card.secondaryActions.length > 0 && (
            <div className="replayer-club-page__video-secondary-actions">
              {card.secondaryActions.map(action => (
                <button
                  className="replayer-club-page__video-secondary"
                  disabled={action.disabled}
                  key={action.id}
                  onClick={action.onClick}
                  type="button"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

PublicPartnerSkyworkVideoCard.propTypes = {
  card: PropTypes.shape({
    accessSummary: PropTypes.string,
    badges: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      tone: PropTypes.string
    })).isRequired,
    description: PropTypes.string,
    duration: PropTypes.string,
    fieldSport: PropTypes.string,
    logoAlt: PropTypes.string,
    logoUrl: PropTypes.string,
    metadata: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })).isRequired,
    metadataAriaLabel: PropTypes.string.isRequired,
    pbvisionEligible: PropTypes.bool.isRequired,
    primaryAction: PropTypes.shape({
      disabled: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      tone: PropTypes.string
    }).isRequired,
    secondaryActions: PropTypes.arrayOf(PropTypes.shape({
      disabled: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })).isRequired,
    showPlayIcon: PropTypes.bool.isRequired,
    sourceLabel: PropTypes.string.isRequired,
    thumbnailAlt: PropTypes.string.isRequired,
    thumbnailFallbackUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    title: PropTypes.string.isRequired,
    tone: PropTypes.string
  }).isRequired
};

export default PublicPartnerSkyworkVideoCard;
